import React, { useState } from "react";
import axios from "axios";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { useApp, API } from "@/contexts/AppContext";
import SectionHeading from "@/components/SectionHeading";
import { CONTACT } from "@/constants/testIds";
import { toast } from "sonner";

export default function ContactPage() {
  const { t, selectedScenarios, requiredProducts } = useApp();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t.contact.error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        scenarios: selectedScenarios,
        products: requiredProducts.map((p) => p.slug),
      });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-10 py-16 sm:py-24">
      <SectionHeading
        label={t.contact.label}
        title={t.contact.title}
        subtitle={t.contact.subtitle}
      />

      <div className="mt-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-6">
          <ContactInfo
            icon={<Mail className="h-4 w-4" />}
            label="Email"
            value="hello@cihazpark.com"
          />
          <ContactInfo
            icon={<Phone className="h-4 w-4" />}
            label="Phone"
            value="+90 850 000 00 00"
          />
          <ContactInfo
            icon={<MapPin className="h-4 w-4" />}
            label="Cihazpark"
            value="İstanbul, Türkiye"
          />
        </div>

        <form
          data-testid={CONTACT.form}
          onSubmit={submit}
          className="md:col-span-7 rounded-3xl border border-white/[0.07] bg-[#0B0F19] p-6 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          <Field
            testId={CONTACT.name}
            value={form.name}
            onChange={(v) => update("name", v)}
            label={t.contact.name}
            required
          />
          <Field
            testId={CONTACT.email}
            type="email"
            value={form.email}
            onChange={(v) => update("email", v)}
            label={t.contact.email}
            required
          />
          <div className="sm:col-span-2">
            <Field
              testId={CONTACT.phone}
              value={form.phone}
              onChange={(v) => update("phone", v)}
              label={t.contact.phone}
            />
          </div>
          <div className="sm:col-span-2">
            <Field
              testId={CONTACT.message}
              value={form.message}
              onChange={(v) => update("message", v)}
              label={t.contact.message}
              textarea
            />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <button
              data-testid={CONTACT.submit}
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full px-7 py-3.5 transition-all hover:shadow-[0_0_30px_rgba(245,158,11,0.45)] disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {t.contact.submit}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function ContactInfo({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/[0.07] bg-[#0B0F19] p-5 flex items-start gap-4">
      <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-1">
          {label}
        </div>
        <div className="text-white font-heading">{value}</div>
      </div>
    </div>
  );
}

function Field({ testId, label, value, onChange, type = "text", required, textarea }) {
  const baseClass =
    "w-full bg-[#11151F] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/60 focus:ring-2 focus:ring-amber-500/15 transition-colors";
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2 font-bold">
        {label}
      </span>
      {textarea ? (
        <textarea
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          required={required}
          className={baseClass + " resize-none"}
        />
      ) : (
        <input
          data-testid={testId}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className={baseClass}
        />
      )}
    </label>
  );
}
